import {
  fade,
  type CrossfadeParams as SvelteCrossfadeParams,
  type TransitionConfig,
} from 'svelte/transition';
import {
  Animation,
  SPRING,
  animationSpeed,
  getTranslate,
} from './utils.svelte';
import { springIn } from './springs';
import type { Coord, Intersections, Match } from '~/types';
import { tick } from 'svelte';
import { getWordCenter } from '~/board/utils';

type ClientRectMap = Map<any, DOMRect>;

export type CrossfadeParams = {
  key: string;
  index: number;
  origin?: Coord;
  scale?: number;
  coords?: Coord;
  shadow?: boolean;
};

type Params = SvelteCrossfadeParams & CrossfadeParams;

type ReceiveParams = SvelteCrossfadeParams & {
  key: string;
  index: number;
};

type Transition = {
  duration: number;
  tickToValue: (t: number) => number;
};

type SendOrReceive<Params> = (
  node: HTMLElement,
  params: Params,
) => () => TransitionConfig;

const lerp = (f: (t: number) => number, t: number, d1: number, d2: number) =>
  f(t) * d2 + (1 - f(t)) * d1;

const constant = (duration: number, constant: number) => ({
  duration,
  tickToValue: (_: number) => constant,
});

const linear = (duration: number, slope: number, intercept = 0) => ({
  duration,
  tickToValue: (t: number) => t * slope + intercept,
});

const piecewise = (
  pieces: Transition[],
  norm?: number[],
  bounds?: [number, number][],
  debug = false,
) => {
  const totalDur = pieces.reduce((a, b) => a + b.duration, 0);
  const accum = pieces.map(
    (_, i, a) => a.slice(0, i).reduce((a, b) => a + b.duration, 0) / totalDur,
  );
  accum.push(1);
  return (t: number) => {
    let idx = accum.findIndex((d) => t < d) - 1;
    if (idx === -2) idx = pieces.length - 1;
    const t0 = accum[idx];
    const t1 = accum[idx + 1];
    const dt = t1 - t0;
    // recreate [0,1] range within segment
    const line = (t: number) => (t - t0) / dt;
    const tick = (t: number) =>
      pieces[idx].tickToValue(line(t)) / (Array.isArray(norm) ? norm[idx] : 1);

    const [y0, y1] = Array.isArray(bounds?.[idx]) ? bounds[idx] : [0, 1];

    const v = lerp(tick, t, y0, y1);
    if (debug) {
      console.log({ idx, t, t1: line(t), v, b: [y0, y1] });
    }
    return v;
  };
};

// Grow a tile's text (letter, score, multiplier) over a transition with `transform: scale`, sampled
// like Svelte samples `css` (60 keyframes per second). Animating `font-size` instead would re-lay-out
// the text every frame on the main thread and keep the whole transition off the compositor.
const growText = (
  node: HTMLElement,
  duration: number,
  factor: (u: number) => number,
) => {
  const n = Math.max(1, Math.ceil(duration / (1000 / 60)));
  const keyframes = Array.from({ length: n + 1 }, (_, i) => ({
    transform: `scale(${factor(i / n)})`,
  }));
  // Svelte starts a transition's real animation when a zero-length placeholder animation finishes,
  // so start this one the same way to begin in the same frame as the tile's own animation.
  const gate = node.animate([], { duration: 0 });
  gate.onfinish = () => {
    for (const el of node.querySelectorAll<HTMLElement>('.tile > span')) {
      // the score and multiplier are anchored to the tile's corners
      el.style.transformOrigin = el.classList.contains('score')
        ? 'right bottom'
        : el.classList.contains('multiplier')
          ? 'left top'
          : 'center';
      el.animate(keyframes, { duration, fill: 'forwards' });
    }
  };
};

export const growAnimation = new Animation();
export const sendWordAnimation = new Animation();
export const scoreWordAnimation = new Animation();

const FADE_DUR = 250;
const SPRING_UP_WEIGHT = 50;

export function crossfade(): [
  SendOrReceive<CrossfadeParams>,
  SendOrReceive<ReceiveParams>,
] {
  const to_receive: ClientRectMap = new Map();
  const to_send: ClientRectMap = new Map();

  function crossfade(
    from: DOMRect,
    node: HTMLElement,
    params: Params,
    intro: boolean,
  ) {
    const to = node.getBoundingClientRect();
    // the inherited text size, before this transition changes anything
    const baseFontSize = parseFloat(getComputedStyle(node).fontSize) || to.width * 0.4;
    const to_center_x = to.width / 2;
    const to_center_y = to.height / 2;
    // TODO this should be pre-computed
    const dw = from.width / to.width;
    // const dh = from.height / to.height;
    const ddw = (from.width - to.width) / 2;
    const ddh = (from.height - to.height) / 2;
    const dx = from.left - to.left + ddw;
    const dy = from.top - to.top + ddh;
    const d = Math.sqrt(dx * dx + dy * dy);

    const [origin_x, origin_y] = params.origin ?? [0, 0];
    const [translate_x, translate_y] = getTranslate(node);

    const fps = animationSpeed.spring / 2;
    const springUp = springIn(0, SPRING_UP_WEIGHT, { ...SPRING, fps });
    const springDown = springIn(0, d, { ...SPRING, fps });

    // TODO this is buggy, params shouldn't be stale
    // params.index may be stale (captured at last render before crossing.send was called),
    // so fall back to the live crossing.tiles value which is always current at transition time.
    const index = params.index ?? crossing.get(params.key).index ?? 0;
    const delay = 25 * index;
    const opacityIn = [
      constant(delay + springUp.duration + springDown.duration, 0),
      constant(FADE_DUR, 1),
    ];
    const opacityOut = [
      constant(delay + springUp.duration + springDown.duration, 1),
      linear(FADE_DUR, -1, 1),
    ];
    const opacityShadow = [
      constant(delay, 0),
      springUp,
      springDown,
      constant(FADE_DUR, 0),
    ];
    const position = [
      constant(delay, 0),
      constant(springUp.duration, 0),
      springDown,
      constant(FADE_DUR, 1),
    ];
    const scale = [
      constant(delay, 1),
      springUp,
      springDown,
      constant(FADE_DUR, dw),
    ];
    const fontSize = [
      constant(delay + springUp.duration, 1),
      springDown,
      constant(FADE_DUR, 1),
    ];
    const duration = position.reduce((a, b) => a + b.duration, 0);

    // console.log('dw', dw);
    const piecewiseScale = piecewise(
      scale,
      [1, SPRING_UP_WEIGHT, d, 1],
      [
        [1, 1],
        [1, 1.25],
        [1.25, dw],
      ],
    );
    const piecewisePosition = piecewise(position, [1, 1, d, 1]);
    const piecewiseOrigin = piecewise(position, [1, 1, d, 1]);
    const piecewiseOpacityOut = piecewise(opacityOut);
    const piecewiseOpacityIn = piecewise(opacityIn);
    const piecewiseOpacityShadow = piecewise(
      opacityShadow,
      [1, SPRING_UP_WEIGHT, d, 1],
      [
        [0, 0],
        [0, 1],
        [1, 0],
      ],
    );
    const piecewiseFontSize = piecewise(
      fontSize,
      [1, d, 1],
      [
        [1, 1],
        [1, 1.25],
        [1.25, 1.25],
      ],
    );

    if (!intro) {
      growAnimation.queue(delay + springUp.duration);
      sendWordAnimation.queue(
        delay + springUp.duration + springDown.duration, // * 0.6,
      );
      scoreWordAnimation.queue(duration);
    }
    const zIndex = params.shadow ? 799 : 800;

    if (intro) {
      return {
        duration,
        css: (t: number, u: number) => `
          z-index: ${zIndex};
          opacity: ${params.shadow ? piecewiseOpacityShadow(u) : piecewiseOpacityIn(t)};
          transform-origin: center;
        `,
      };
    }

    // Keep the fly-out on the compositor: its keyframes only animate opacity/translate/transform.
    // z-index and a fixed 0 0 transform-origin are set once (the node is removed afterwards), the
    // moving scale origin is folded into the translation, and the text grows via `growText`.
    node.style.zIndex = `${zIndex}`;
    node.style.transformOrigin = '0 0';
    growText(
      node,
      duration,
      (u) => (piecewiseFontSize(u) * to.width * 0.4) / baseFontSize,
    );
    return {
      duration,
      css: (t: number, u: number) => {
        const s = piecewiseScale(u);
        // scaling about origin (ox, oy) == translating by origin * (1 - s), then scaling about 0 0
        const ox = lerp(piecewiseOrigin, u, origin_x - translate_x, to_center_x);
        const oy = lerp(piecewiseOrigin, u, origin_y - translate_y, to_center_y);
        const x = lerp(piecewisePosition, u, translate_x, translate_x + dx) + ox * (1 - s);
        const y = lerp(piecewisePosition, u, translate_y, translate_y + dy) + oy * (1 - s);
        return `
          opacity: ${params.shadow ? piecewiseOpacityShadow(u) : piecewiseOpacityOut(u)};
          translate: none;
          transform: translate3d(${x}px, ${y}px, 0) scale(${s});
        `;
      },
    };
  }

  function transition(
    items: ClientRectMap,
    counterparts: ClientRectMap,
    intro: boolean,
  ) {
    return (node: HTMLElement, params: Params) => {
      items.set(params.key, node.getBoundingClientRect());
      return () => {
        if (counterparts.has(params.key)) {
          const from = counterparts.get(params.key);
          counterparts.delete(params.key);
          return crossfade(from!, node, params, intro);
        }
        if (intro) {
          // Receive side found no sender yet — leave registration intact so
          // the out:send can still find this element when it fires later.
          return { duration: 0, css: () => '' };
        }
        // Send side: element is leaving with no destination, clean up and fade.
        items.delete(params.key);
        return fade(node);
      };
    };
  }

  return [
    transition(to_send, to_receive, false), // Out transition
    transition(to_receive, to_send, true), // In transition
  ];
}

export const [send, receive] = crossfade();

class Crossfade {
  origin: Coord = [0, 0];
  tiles: Record<string, CrossfadeParams> = {};
  shadows: Array<CrossfadeParams> = $state([]);

  get(id: string) {
    return this.tiles[id] ?? {};
  }

  send(word: Match, intersections: Intersections) {
    this.tiles = {};
    // Scale around this word's own intersections, if it has any, else its center. `intersections`
    // covers every word scored this turn, so match by coordinate (the last word through a shared tile
    // keeps the board tile's id, which isn't a key in `intersections`).
    const shared = new Set(Object.values(intersections).map(({ coord }) => coord.join()));
    const ownIntersections = word.coords.filter((c) => shared.has(c.join()));
    this.origin = getWordCenter(ownIntersections.length ? ownIntersections : word.coords);

    for (const [i, tile] of Object.entries(word.word)) {
      const c = {
        key: tile.id,
        scale: 1,
        origin: this.origin,
        index: word.axis === 'row' ? +i : word.word.length - +i,
        coords: word.coords[+i],
      };
      this.tiles[tile.id] = c;
      this.shadows.push(c);
    }
  }

  clear() {
    this.shadows = [];
  }

  // Fly a scored word's tiles to the word pile. The tiles must leave in the same update that mounts
  // the word's receiving tiles, or `receive` finds no sender and appears immediately. The shadows
  // mount in that update and leave in the next one; both run in the same task, so all of their
  // animations still start in the same frame.
  async launch(word: Match, intersections: Intersections, removeLetters: () => void) {
    this.send(word, intersections);
    removeLetters();
    // mount the shadows so they can animate out
    await tick();
    this.clear();
  }
}

export const crossing = new Crossfade();
